import orderBy from 'lodash-es/orderBy';
import assign from 'lodash-es/assign';
import * as moment from 'moment';
import { ILocationOrder, IOrder } from '../state/orders';
import { DateFormat } from '@telstra-connect/utils/data/date-helpers';

const statusSorting = {
  '-status':
  {
    'On track': 0,
    'Delayed': 1,
    'Completed': 2,
    'Withdrawn': 3
  },
  '+status':
  {
    'Completed': 0,
    'Delayed': 1,
    'On track': 2,
    'Withdrawn': 3
  }
}

let sortStatus: IOrdersFilter['sort'];

function customStatus(item: IOrder): string {
  const jsonStatus = (typeof sortStatus !== 'undefined' && statusSorting[sortStatus] !== undefined) ?
    statusSorting[sortStatus] : statusSorting['-status'];
  return jsonStatus[item.status];
}

function customSalesOrderNo(item: IOrder): number {
  return item.number ? Number(item.number.replace(/\D/g, '')) : 0;
}

function customName(item: IOrder): string {
  return item.name ? item.name.toLowerCase() : '';
}

function customProductType(item: ILocationOrder): string {
  return item.productType ? item.productType.toLowerCase() : '';
}

function customSiteAddress(item: ILocationOrder): string {
  return item.siteAddress ? item.siteAddress.toLowerCase() : '';
}

function customSiteName(item: ILocationOrder): string {
  return item.siteName ? item.siteName.toLowerCase() : '';
}

function customCustomerName(item: IOrder): string {
  return item.customerName ? item.customerName.toLowerCase() : '';
}

function customCidn(item: IOrder): string {
  return item.cidn ? item.cidn.toLowerCase() : '';
}

function customNcp(item: IOrder): string {
  if (item.ncp) {
    return item.ncp.toLowerCase();
  }
}

function customSiteLocation(item: ILocationOrder): string {
  return item.location ? item.location.toLowerCase() : '';
}

function customProjectManager(item: IOrder): string {
  return item.projectManager ? item.projectManager.toLowerCase() : '';
}

function customYourReferencen(item: IOrder): string {
  return item.yourReference ? item.yourReference.toLowerCase() : '';
}

function customSitesCompleted(item: IOrder): number {
  return item.sitesComplete ? Number(item.sitesComplete.split('/')[0]) : 0;
}

function customEstimatedDelivery(item: IOrder): string {
  const momentObj = moment.utc(item.estimatedDelivery, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customOrderClosedDate(item: IOrder): string {
  const momentObj = moment.utc(item.closedDate, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customOrderRequiredDate(item: IOrder): string {
  const momentObj = moment.utc(item.requiredDelivery, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customProductEstimatedDelivery(item: ILocationOrder): string {
  const momentObj = moment.utc(item.productEstimatedDelivery, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customProductOrderClosedDate(item: ILocationOrder): string {
  const momentObj = moment.utc(item.productClosedDate, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customProductOrderRequiredDate(item: ILocationOrder): string {
  const momentObj = moment.utc(item.productRequiredDelivery, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customUpcomingAppointment(item: ILocationOrder): string {
  const momentObj = moment.utc(item.upcomingAppointment, DateFormat.DEFAULT);
  return momentObj.format('YYYY-MM-DD');
}

function customProductStatus(item: ILocationOrder): string {
  const jsonStatus = (typeof sortStatus !== 'undefined' && statusSorting[sortStatus] !== undefined) ?
    statusSorting[sortStatus] : statusSorting['-status'];
  return jsonStatus[item.productStatus];
}

interface IOrdersFilter {
  activeTab: 'Orders' | 'Locations';
  sort?: string;
}

export function sortOrders(filter: IOrdersFilter,
  orders: IOrder[]): IOrder[] {
  sortStatus = filter.sort;
  const field = sortStatus && sortStatus.substr(1) || '';

  let defaultSortKeys = {
    status: { calc: customStatus, direction: 'asc' },
    location: { calc: customSiteLocation, direction: 'asc' },
    name: { calc: customName, direction: 'asc' },
    ncp: { calc: customNcp, direction: 'asc' },
    estimatedDelivery: { calc: customEstimatedDelivery, direction: 'asc' },
    sitesComplete: { calc: customSitesCompleted, direction: 'asc' },
    number: { calc: customSalesOrderNo, direction: 'asc' },
    closedDate: { calc: customOrderClosedDate, direction: 'asc' },
    requiredDelivery: { calc: customOrderRequiredDate, direction: 'asc' },
    customerName: { calc: customCustomerName, direction: 'asc' },
    cidn: { calc: customCidn, direction: 'asc' },
    yourReference: { calc: customYourReferencen, direction: 'asc' },
    projectManager: { calc: customProjectManager, direction: 'asc' }
  };

  if (sortStatus && field in defaultSortKeys) {
    const sortField = defaultSortKeys[field];
    delete defaultSortKeys[field];
    sortField.direction = (field !== 'status' && sortStatus.substr(0, 1) === '-') ? 'desc' : 'asc';

    const sortByfield = {};
    sortByfield[field] = sortField;
    defaultSortKeys = assign(sortByfield, defaultSortKeys);
  }

  const sortByKeys: string[] = [];
  const sortByDirection: string[] = [];

  Object.keys(defaultSortKeys).forEach((key) => {
    sortByKeys.push(defaultSortKeys[key].calc);
    sortByDirection.push(defaultSortKeys[key].direction);
  });

  return orderBy(orders,
    sortByKeys, sortByDirection);
}

export function sortLocationOrders(filter: IOrdersFilter,
  orders: ILocationOrder[]): ILocationOrder[] {
  sortStatus = filter.sort;
  const field = sortStatus && sortStatus.substr(1) || '';

  let defaultSortKeys = {
    location: { calc: customSiteLocation, direction: 'asc' },
    productStatus: { calc: customProductStatus, direction: 'asc' },
    name: { calc: customName, direction: 'asc' },
    productType: { calc: customProductType, direction: 'asc' },
    productEstimatedDelivery: { calc: customProductEstimatedDelivery, direction: 'asc' },
    siteAddress: { calc: customSiteAddress, direction: 'asc' },
    siteName: { calc: customSiteName, direction: 'asc' },
    productClosedDate: { calc: customProductOrderClosedDate, direction: 'asc' },
    productRequiredDelivery: { calc: customProductOrderRequiredDate, direction: 'asc' },
    upcomingAppointment: { calc: customUpcomingAppointment, direction: 'asc'},
    number: { calc: customSalesOrderNo, direction: 'asc' },
    ncp: { calc: customNcp, direction: 'asc' },
    customerName: { calc: customCustomerName, direction: 'asc' },
    cidn: { calc: customCidn, direction: 'asc' }
  };

  if (sortStatus && field in defaultSortKeys) {
    const sortField = defaultSortKeys[field];
    delete defaultSortKeys[field];
    sortField.direction = (field !== 'status' && sortStatus.substr(0, 1) === '-') ? 'desc' : 'asc';

    const sortByfield = {};
    sortByfield[field] = sortField;
    defaultSortKeys = assign(sortByfield, defaultSortKeys);
  }

  defaultSortKeys = {
    location: { calc: customSiteLocation, direction: 'asc' },
    ...defaultSortKeys
  }

  const sortByKeys: string[] = [];
  const sortByDirection: string[] = [];

  Object.keys(defaultSortKeys).forEach((key) => {
    sortByKeys.push(defaultSortKeys[key].calc);
    sortByDirection.push(defaultSortKeys[key].direction);
  });

  return orderBy(orders,
    sortByKeys, sortByDirection);
}
